import axios from "axios";
import { Buffer } from "buffer";
import { casaServiceBaseUrl } from "../utils/config";
import { redirectToLogin } from "../utils/browserUtils";
import { apiTrace } from "../utils/apiTrace";
import { getMarvinToken, getRegionID } from "../utils/authUtils";
import { isMiP } from "../utils/helper";
import { attachPayloadCrypto } from "./payloadCrypto";

// Function to get the authorization token
const axiosInstanceWeb = axios.create({
  baseURL: casaServiceBaseUrl,
  headers: {
    Accept: "*/*",
    "Content-Type": "application/json",
    "User-Agent": false,
  },
  // Kept true for the MiP path, which authenticates by cookie. The branch below switches the
  // non-MiP path to fetch + 'omit'; unlike casa-lead-360 there is no else here, so MiP relies on
  // this default.
  withCredentials: true
});

// Payload encryption (FSP-5412). Registered before the response interceptor below on purpose:
// axios runs response interceptors in registration order, so this one opens a sealed body before
// the handler below reads it, and retries a 409 from an expired session before anything can
// surface it to the user. It is a no-op outside the NextGen custom-domain deployment.
attachPayloadCrypto(axiosInstanceWeb);

if (!isMiP() && getMarvinToken() && getRegionID()) {
  // Use the fetch adapter so same-origin requests don't carry cookies. XHR always sends them
  // same-origin regardless of withCredentials; fetch honours credentials: 'omit'. Requires
  // axios >= 1.7. Mirrors casa-lead-360 11ef902.
  axiosInstanceWeb.defaults.adapter = "fetch";
  axiosInstanceWeb.defaults.withCredentials = "omit";
  axiosInstanceWeb.defaults.headers.common["x-lsq-marvin-token"] = getMarvinToken();
  axiosInstanceWeb.defaults.headers.common["x-lsq-regionId"] = getRegionID();
} 

// Add a request interceptor to capture the start time  
axiosInstanceWeb.interceptors.request.use(config => {  
  config.startTime = performance.now(); // Record the start time  
  return config;
}); 

axiosInstanceWeb.interceptors.response.use(
  (response) => {
    const endTime = performance.now(); // Record the end time  
    const duration = endTime - response.config.startTime; 
    response.responseTime = duration;
    if (response.headers?.authorization) {
      const encryptedToken = Buffer.from(
        response.headers.authorization,
        "utf-8"
      ).toString("base64");
      sessionStorage.setItem("casa-authorization-token", encryptedToken);
      axiosInstanceWeb.defaults.headers.common["authorization"] = Buffer.from(
        encryptedToken,
        "base64"
      ).toString("utf-8");
    }
    apiTrace(response);
    return response;
  },
  (error) => {
    if (error?.response?.status === 401) {
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

export default axiosInstanceWeb;
